import { Badge, type ColorDeBadge } from '@/shared/components/Badge'
import { formatDate, formatDateTimeMX } from '@/shared/lib/dateUtils'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useState, useEffect } from "react";
import {
  FileText,
  Calendar,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Paperclip,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  File,
  User,
  Building2,
  Receipt,
  DollarSign,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { purchaseOrdersService } from '@/features/proveedores/api/purchaseOrders.service';
import { getSupplierProfile } from '@/features/proveedores/api/supplierProfile.service';
import { pasoDeFleteResuelto } from '@/features/proveedores/lib/documentosDeFlete';
import { CapaDeModal } from '@/shared/components/CapaDeModal';
import { ArchivoDeFlete } from '@/features/proveedores/components/ArchivoDeFlete';
import type {
  PurchaseOrder,
  PurchaseOrderStats,
  PurchaseOrderStatus,
  PaginationMeta,
  PurchaseOrderAttachment,
  PurchaseOrderComment,
  PurchaseOrderInvoice,
} from '@/types/purchaseOrder.types';
import toast from "react-hot-toast";
import BloqueoFacturacion, { type BloqueoFacturacionError } from '@/features/proveedores/components/BloqueoFacturacion';
import { TarjetaDeFactura } from '@/features/proveedores/components/TarjetaDeFactura';
import { EsqueletoTarjetasTexto } from '@/shared/components/Esqueleto';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

const PurchaseOrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [stats, setStats] = useState<PurchaseOrderStats | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | "ALL">(
    "ALL"
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmOrderId, setConfirmOrderId] = useState<string | null>(null);
  const [confirmOrderNumber, setConfirmOrderNumber] = useState("");
  const [confirmComment, setConfirmComment] = useState("");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectOrderId, setRejectOrderId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentOrderId, setCommentOrderId] = useState<string | null>(null);
  const [commentOrderNumber, setCommentOrderNumber] = useState("");
  const [commentName, setCommentName] = useState("");
  const [commentText, setCommentText] = useState("");
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  /**
   * Paso de documentos de flete (SP-221). Solo lo ven los proveedores de tipo
   * Fletes: para General y Servicios el flujo de subida no cambia.
   */
  const [esFletes, setEsFletes] = useState(false);
  // Bloqueo de facturacion (422 del back): null = sin bloqueo activo.
  const [bloqueoError, setBloqueoError] = useState<BloqueoFacturacionError | null>(null);

  /**
   * Revisa si puede facturar ANTES de abrir el modal de subir.
   *
   * Antes el proveedor se enteraba al final, con el XML ya elegido y validado.
   * El 422 del upload sigue puesto por si se bloquea entre el clic y el envio.
   */
  const puedeFacturar = async (): Promise<boolean> => {
    const estado = await purchaseOrdersService.getEstadoDeFacturacion();
    if (!estado.bloqueado) return true;
    const { bloqueado: _ignorado, ...cuerpo } = estado;
    setBloqueoError(cuerpo);
    return false;
  };
  const [cartaPorteAplica, setCartaPorteAplica] = useState(false);
  const [cartaPorteXml, setCartaPorteXml] = useState<File | null>(null);
  const [cartaPortePdf, setCartaPortePdf] = useState<File | null>(null);
  const [remisionFirmada, setRemisionFirmada] = useState<File | null>(null);
  const [hojaEntregas, setHojaEntregas] = useState<File | null>(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [attachmentOrderId, setAttachmentOrderId] = useState<string | null>(
    null
  );
  const [attachmentOrderNumber, setAttachmentOrderNumber] = useState("");
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentType, setAttachmentType] = useState("");
  const [attachmentDescription, setAttachmentDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [viewAttachmentsModalOpen, setViewAttachmentsModalOpen] =
    useState(false);
  const [, setViewAttachmentsOrderId] = useState<
    string | null
  >(null);
  const [viewAttachmentsOrderNumber, setViewAttachmentsOrderNumber] =
    useState("");
  const [attachmentsList, setAttachmentsList] = useState<
    PurchaseOrderAttachment[]
  >([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [viewCommentsModalOpen, setViewCommentsModalOpen] = useState(false);
  const [viewCommentsOrderId, setViewCommentsOrderId] = useState<string | null>(
    null
  );
  const [viewCommentsOrderNumber, setViewCommentsOrderNumber] = useState("");
  const [commentsList, setCommentsList] = useState<PurchaseOrderComment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [showAddCommentInView, setShowAddCommentInView] = useState(false);
  const [viewInvoicesModalOpen, setViewInvoicesModalOpen] = useState(false);
  const [viewInvoicesOrderId, setViewInvoicesOrderId] = useState<string | null>(
    null
  );
  const [viewInvoicesOrderNumber, setViewInvoicesOrderNumber] = useState("");
  const [invoicesList, setInvoicesList] = useState<PurchaseOrderInvoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  useEffect(() => {
    loadOrders();
  }, [pagination.page, statusFilter, startDate, endDate]);

  // Esta pantalla la usan el portal del proveedor y la vista interna de Compras.
  // El paso de flete es del proveedor, así que solo se pregunta por su tipo en el
  // portal: adentro podría quedar un token de proveedor viejo en el navegador y
  // el paso aparecería con los datos de alguien más.
  const enPortalDeProveedor = useLocation().pathname.startsWith('/proveedores');

  useEffect(() => {
    if (!enPortalDeProveedor) {
      setEsFletes(false);
      return;
    }
    // Si la consulta falla se asume que no es de Fletes: el flujo de siempre.
    getSupplierProfile()
      .then((perfil) => setEsFletes(perfil.general?.supplierType === 'fletes'))
      .catch(() => setEsFletes(false));
  }, [enPortalDeProveedor]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const filters: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (statusFilter !== "ALL") filters.status = statusFilter;
      if (startDate) filters.from_date = new Date(startDate).toISOString();
      if (endDate) filters.to_date = new Date(endDate).toISOString();

      const response = await purchaseOrdersService.getOrders(filters);
      setOrders(response.data);
      setPagination(response.meta);

      const statsData = await purchaseOrdersService.getStats(response.data);
      setStats(statsData);
    } catch (error) {
      console.error("Error loading orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: PurchaseOrderStatus) => {
    const badges: Record<string, { color: ColorDeBadge; label: string }> = {
      draft:       { color: "gris",     label: "Borrador"    },
      pending:     { color: "amarillo", label: "Pendiente"   },
      confirmed:   { color: "verde",    label: "Confirmada"  },
      rejected:    { color: "rojo",     label: "Rechazada"   },
      in_progress: { color: "azul",     label: "En Progreso" },
      completed:   { color: "verde",    label: "Completada"  },
      cancelled:   { color: "rojo",     label: "Cancelada"   },
    };

    const badge = badges[status];
    if (!badge) return null;

    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  const getFulfillmentBadge = (status?: string) => {
    if (!status) return null;

    let color: ColorDeBadge = "gris";
    if (status === "Surtida") color = "verde";
    else if (status === "Parcialmente surtida") color = "amarillo";

    return <Badge color={color}>{status}</Badge>;
  };

  const getPaymentBadge = (status?: string) => {
    if (!status) return null;

    let color: ColorDeBadge = "gris";
    if (status === "Pagado") color = "verde";
    else if (status === "Parcialmente pagado") color = "amarillo";

    return <Badge color={color}>{status}</Badge>;
  };

  const handleMenuAction = (orderId: string, action: string) => {
    setOpenMenuId(null);
    const order = orders.find((o) => o.id === orderId);

    switch (action) {
      case "confirm":
        setConfirmOrderId(orderId);
        setConfirmOrderNumber(order?.po_number || "");
        setConfirmModalOpen(true);
        break;
      case "reject":
        setRejectOrderId(orderId);
        setRejectModalOpen(true);
        break;
      case "comment":
        setCommentOrderId(orderId);
        setCommentOrderNumber(order?.po_number || "");
        setCommentModalOpen(true);
        break;
      case "invoice":
        void (async () => {
          if (!(await puedeFacturar())) return;
          setInvoiceOrderId(orderId);
          setInvoiceModalOpen(true);
        })();
        break;
      case "evidence": {
        const orderForAttachment = orders.find((o) => o.id === orderId);
        setAttachmentOrderId(orderId);
        setAttachmentOrderNumber(orderForAttachment?.po_number || "");
        setAttachmentModalOpen(true);
        break;
      }
      case "viewInvoices":
        handleViewInvoices(orderId, order?.po_number || "");
        break;
    }
  };

  const handleConfirmSubmit = async () => {
    if (confirmOrderId) {
      try {
        await purchaseOrdersService.confirmOrder(
          confirmOrderId,
          confirmComment || undefined
        );
        setConfirmModalOpen(false);
        setConfirmComment("");
        setConfirmOrderId(null);
        setConfirmOrderNumber("");
        loadOrders();
      } catch (error) {
        console.error("Error confirming order:", error);
        toast.error("Error al confirmar la orden");
      }
    }
  };

  const handleRejectSubmit = async () => {
    if (rejectOrderId && rejectReason.trim()) {
      try {
        await purchaseOrdersService.rejectOrder(rejectOrderId, rejectReason);
        setRejectModalOpen(false);
        setRejectReason("");
        setRejectOrderId(null);
        loadOrders();
      } catch (error) {
        console.error("Error rejecting order:", error);
        toast.error("Error al rechazar la orden");
      }
    }
  };

  const handleCommentSubmit = async () => {
    if (commentOrderId && commentText.trim()) {
      try {
        await purchaseOrdersService.addComment(commentOrderId, commentText);
        setCommentModalOpen(false);
        setCommentName("");
        setCommentText("");
        setCommentOrderId(null);
        setCommentOrderNumber("");
        loadOrders();
      } catch (error) {
        console.error("Error adding comment:", error);
        toast.error("Error al agregar el comentario");
      }
    }
  };

  const handleCommentSubmitInView = async () => {
    if (viewCommentsOrderId && commentText.trim()) {
      try {
        await purchaseOrdersService.addComment(
          viewCommentsOrderId,
          commentText
        );
        setCommentText("");
        setShowAddCommentInView(false);
        await loadCommentsInModal();
        loadOrders();
      } catch (error) {
        console.error("Error adding comment:", error);
        toast.error("Error al agregar el comentario");
      }
    }
  };

  const cerrarModalDeFactura = () => {
    setInvoiceModalOpen(false);
    setPdfFile(null);
    setXmlFile(null);
    setInvoiceOrderId(null);
    setCartaPorteAplica(false);
    setCartaPorteXml(null);
    setCartaPortePdf(null);
    setRemisionFirmada(null);
    setHojaEntregas(null);
  };

  const fleteResuelto = pasoDeFleteResuelto(esFletes, {
    cartaPorteAplica,
    cartaPorteXml,
    cartaPortePdf,
    remisionFirmada,
    hojaEntregas,
  });

  const handleInvoiceSubmit = async () => {
    if (invoiceOrderId && xmlFile) {
      try {
        // El servidor exige las líneas que cubre la factura y esta pantalla
        // mandaba la lista vacía: la subida fallaba siempre con "line_ids should
        // not be empty". Se factura contra lo que SLYRSA ya liberó, que es lo
        // único facturable.
        const disponibles = await purchaseOrdersService.getAvailableLines(invoiceOrderId);
        const lineIds = disponibles.map((linea) => linea.id);
        if (lineIds.length === 0) {
          toast.error(
            'Esta orden no tiene líneas liberadas para facturar. SLYRSA tiene que liberarlas primero.',
            { duration: 6000 },
          );
          return;
        }

        await purchaseOrdersService.adjustInvoice(
          invoiceOrderId,
          xmlFile,
          lineIds,
          pdfFile || undefined,
          esFletes
            ? {
                cartaPorteAplica,
                cartaPorteXml,
                cartaPortePdf,
                remisionFirmada,
                hojaEntregas,
              }
            : undefined
        );
        toast.success("Factura subida exitosamente");
        cerrarModalDeFactura();
        loadOrders();
      } catch (errorCrudo: unknown) {
        const error = errorCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          // El 422 de bloqueo trae su propio cuerpo, con el tipo de bloqueo y el
          // mes, por eso `data` es más ancho que un simple mensaje.
          response?: {
            status?: number;
            data?: {
              message?: string | string[];
              tipo?: string;
              type?: string;
              [clave: string]: unknown;
            };
          };
        };
        console.error("Error uploading invoice:", error);
        // El cliente HTTP lanza { message, statusCode } y no la forma de axios,
        // asi que se leen las dos.
        const data = (error.response?.data ?? error) as {
          message?: string | string[];
          tipo?: string;
          type?: string;
          [clave: string]: unknown;
        };
        const status = error.response?.status ?? error.statusCode;
        // 422 de bloqueo: el proveedor tiene que enterarse de QUE lo bloquea y
        // como desbloquearse, no de un "error al subir la factura". Desde esta
        // pantalla tambien se sube factura, y aqui ese mensaje no existia.
        if (status === 422 && (typeof data?.tipo === 'string' || typeof data?.type === 'string')) {
          cerrarModalDeFactura();
          setBloqueoError(data);
        } else {
          const errorMessage = data?.message || "Error al subir la factura";
          toast.error(Array.isArray(errorMessage) ? errorMessage.join(' ') : errorMessage, {
            duration: 5000,
            position: "bottom-right",
          });
        }
      }
    }
  };

  const handleAttachmentSubmit = async () => {
    if (attachmentOrderId && attachmentFile) {
      setIsUploading(true);
      try {
        await purchaseOrdersService.uploadAttachment(
          attachmentOrderId,
          attachmentFile,
          attachmentType || undefined,
          attachmentDescription || undefined
        );
        setAttachmentModalOpen(false);
        setAttachmentFile(null);
        setAttachmentType("");
        setAttachmentDescription("");
        setAttachmentOrderId(null);
        setAttachmentOrderNumber("");
        loadOrders();
      } catch (error) {
        console.error("Error uploading attachment:", error);
        toast.error("Error al subir el archivo");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleViewAttachments = async (
    orderId: string,
    orderNumber: string
  ) => {
    setViewAttachmentsOrderId(orderId);
    setViewAttachmentsOrderNumber(orderNumber);
    setViewAttachmentsModalOpen(true);
    setIsLoadingAttachments(true);

    try {
      const attachments = await purchaseOrdersService.getAttachments(orderId);
      setAttachmentsList(attachments);
    } catch (error) {
      console.error("Error loading attachments:", error);
      toast.error("Error al cargar los adjuntos");
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const getFileIcon = (type: string) => {
    const icons: Record<string, string> = {
      cotizacion: "📄",
      especificaciones: "📋",
      contrato: "📝",
      evidencia: "📎",
      otro: "📁",
    };
    return icons[type] || "📄";
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cotizacion: "Cotización",
      especificaciones: "Especificaciones",
      contrato: "Contrato",
      evidencia: "Evidencia",
      otro: "Otro",
    };
    return labels[type] || type;
  };

  const handleViewComments = async (orderId: string, orderNumber: string) => {
    setViewCommentsOrderId(orderId);
    setViewCommentsOrderNumber(orderNumber);
    setViewCommentsModalOpen(true);
    setIsLoadingComments(true);
    setShowAddCommentInView(false);

    try {
      const comments = await purchaseOrdersService.getComments(orderId);
      setCommentsList(comments);
    } catch (error) {
      console.error("Error loading comments:", error);
      toast.error("Error al cargar los comentarios");
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleViewInvoices = async (orderId: string, orderNumber: string) => {
    setViewInvoicesOrderId(orderId);
    setViewInvoicesOrderNumber(orderNumber);
    setViewInvoicesModalOpen(true);
    setIsLoadingInvoices(true);

    try {
      const invoices = await purchaseOrdersService.getInvoices(orderId);
      setInvoicesList(invoices);
    } catch (error) {
      console.error("Error loading invoices:", error);
      toast.error("Error al cargar las facturas");
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const loadCommentsInModal = async () => {
    if (viewCommentsOrderId) {
      setIsLoadingComments(true);
      try {
        const comments = await purchaseOrdersService.getComments(
          viewCommentsOrderId
        );
        setCommentsList(comments);
      } catch (error) {
        console.error("Error loading comments:", error);
      } finally {
        setIsLoadingComments(false);
      }
    }
  };

  if (isLoading) {
    return (
      // A-01: silueta de la tabla de ordenes.
      <EsqueletoTarjetasTexto cuantas={5} columnas="" campos={6} />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Órdenes de compra</h1>
        <p className="text-sm text-gray-600 mt-1">
          Administra aquí tus órdenes de compra
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Total de órdenes de compras"
            value={stats.total}
            icon={<FileText />}
            variant="secondary"
          />
          <KpiCard
            title="Órdenes confirmadas"
            value={stats.confirmed}
            subtext={`${stats.confirmedPercentage.toFixed(1)}% del total`}
            icon={<CheckCircle2 />}
            variant="success"
          />
          <KpiCard
            title="Órdenes rechazadas"
            value={stats.rejected}
            subtext={`${stats.rejectedPercentage.toFixed(1)}% del total`}
            icon={<XCircle />}
            variant="error"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Estatus"
            todos="Todos los estatus"
            value={statusFilter === "ALL" ? "" : statusFilter}
            onChange={(valor) =>
              setStatusFilter((valor || "ALL") as PurchaseOrderStatus | "ALL")
            }
            opciones={[
              { value: "draft", label: "Borrador" },
              { value: "pending", label: "Pendiente" },
              { value: "confirmed", label: "Confirmada" },
              { value: "rejected", label: "Rechazada" },
              { value: "in_progress", label: "En progreso" },
              { value: "completed", label: "Completada" },
              { value: "cancelled", label: "Cancelada" },
            ]}
            canDelete={false}
            placeholderDeBusqueda="Buscar estatus..."
          />

          {/* Date Filters */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-brand w-40 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-[#315FB9]"
              />
            </div>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-brand w-40 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-[#315FB9]"
              />
            </div>
          </div>

          {/* Clear Button */}
          <button
            onClick={() => {
              setStatusFilter("ALL");
              setStartDate("");
              setEndDate("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 text-sm font-medium underline hover:no-underline transition-all"
            style={{ color: "#315FB9" }}
          >
            Limpiar
          </button>
          <div className="flex-1"></div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-3">
        {orders.map((order) => (
          <div
            key={order.id}
            onClick={() => navigate(`/proveedores/ordenes/${order.id}`)}
            className="bg-white rounded-lg border border-[#E5E7EB] hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <FileText className="w-7 h-7" style={{ color: "#2563eb" }} />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-medium text-[#1E1E1F]">
                      {order.po_number || order.order_number || '—'}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {order.created_at
                        ? formatDate(order.created_at)
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {order.status === "confirmed" && (
                  <>
                    {order.attachments_count > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAttachments(order.id, order.po_number);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                      >
                        <Paperclip className="w-3.5 h-3.5" />(
                        {order.attachments_count})
                      </button>
                    )}
                    {order.comments_count > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewComments(order.id, order.po_number);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />(
                        {order.comments_count})
                      </button>
                    )}
                  </>
                )}
                {getStatusBadge(order.status)}
                {getFulfillmentBadge(order.fulfillment_status)}
                {getPaymentBadge(order.payment_status)}

                {/* Actions Menu */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === order.id ? null : order.id);
                    }}
                    className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                  >
                    <MoreVertical className="w-5 h-5 text-[#2563eb]" />
                  </button>

                  {openMenuId === order.id && (
                    <div
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-xl py-1 z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {order.status === "pending" && (
                        <>
                          <button
                            onClick={() =>
                              handleMenuAction(order.id, "confirm")
                            }
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            Confirmar
                          </button>
                          <button
                            onClick={() => handleMenuAction(order.id, "reject")}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                            Rechazar
                          </button>
                        </>
                      )}
                      {order.status === "confirmed" && (
                        <>
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              navigate(
                                `/proveedores/ordenes/${order.id}/pagos`
                              );
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <DollarSign className="w-4 h-4 text-green-600" />
                            Ver pagos
                          </button>
                          <button
                            onClick={() =>
                              handleMenuAction(order.id, "comment")
                            }
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                            Agregar comentario
                          </button>
                          <button
                            onClick={() =>
                              handleMenuAction(order.id, "invoice")
                            }
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <FileText className="w-4 h-4 text-blue-600" />
                            Adjuntar Factura
                          </button>
                          <button
                            onClick={() =>
                              handleMenuAction(order.id, "viewInvoices")
                            }
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <Receipt className="w-4 h-4 text-green-600" />
                            Ver facturas
                          </button>
                          <button
                            onClick={() =>
                              handleMenuAction(order.id, "evidence")
                            }
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <Paperclip className="w-4 h-4 text-blue-600" />
                            Adjuntar archivo
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 lg:gap-6 pl-11 cursor-pointer">
              <div>
                <p className="text-xs text-gray-500 mb-1">Importe total</p>
                <p className="text-sm font-semibold text-[#1E1E1F]">
                  ${(Number(order.total) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Total pagado</p>
                <p className="text-sm font-semibold text-[#1E1E1F]">
                  {`${(order.currency || 'MXN').toUpperCase()} $${(Number.isFinite(Number(order.total_pagado)) ? Number(order.total_pagado) : 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Fecha requerida</p>
                <p className="text-sm font-semibold text-[#1E1E1F]">
                  {order.requested_date
                    ? formatDate(order.requested_date)
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Moneda</p>
                <p className="text-sm font-semibold text-[#1E1E1F]">{order.currency}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Descripción</p>
                <p className="text-sm font-semibold text-[#1E1E1F] truncate" title={order.description}>
                  {order.description}
                </p>
              </div>
            </div>
            </div>
          </div>
        ))}
      </div>

      {orders.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No se encontraron órdenes de compra</p>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirmar orden
                </h3>
                <button
                  onClick={() => {
                    setConfirmModalOpen(false);
                    setConfirmComment("");
                    setConfirmOrderId(null);
                    setConfirmOrderNumber("");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                ¿Estás seguro de confirmar la orden{" "}
                <span className="font-semibold">{confirmOrderNumber}</span>?
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comentario{" "}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={confirmComment}
                  onChange={(e) => setConfirmComment(e.target.value)}
                  placeholder="Ej: Entrega estimada en 5 días hábiles"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none"
                  style={{ backgroundColor: "#F9FAFB" }}
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setConfirmModalOpen(false);
                    setConfirmComment("");
                    setConfirmOrderId(null);
                    setConfirmOrderNumber("");
                  }}
                  className={estiloDeBoton('secundario', 'flex-1')}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  className={estiloDeBoton('exito', 'flex-1')}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Motivo del rechazo
                </h3>
                <button
                  onClick={() => {
                    setRejectModalOpen(false);
                    setRejectReason("");
                    setRejectOrderId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Ingresa el motivo de rechazo para la orden
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ingresa aquí el motivo de rechazo..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none"
                  style={{ backgroundColor: "#F9FAFB" }}
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setRejectModalOpen(false);
                    setRejectReason("");
                    setRejectOrderId(null);
                  }}
                  className={estiloDeBoton('secundario', 'flex-1')}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={!rejectReason.trim()}
                  className={estiloDeBoton('primario', 'flex-1')}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Comment Modal */}
      {commentModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Agregar comentario
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Agregar comentario para orden de compra {commentOrderNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setCommentModalOpen(false);
                    setCommentName("");
                    setCommentText("");
                    setCommentOrderId(null);
                    setCommentOrderNumber("");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Campo nombre oculto pero mantenido para uso futuro */}
                <div style={{ display: "none" }}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del comentario
                  </label>
                  <input
                    type="text"
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    placeholder="Ingresa el nombre de tu comentario"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    style={{ backgroundColor: "#F9FAFB" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comentario
                  </label>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Ingresa aquí tu comentario"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none"
                    style={{ backgroundColor: "#F9FAFB" }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setCommentModalOpen(false);
                    setCommentName("");
                    setCommentText("");
                    setCommentOrderId(null);
                    setCommentOrderNumber("");
                  }}
                  className={estiloDeBoton('secundario', 'flex-1')}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCommentSubmit}
                  disabled={!commentText.trim()}
                  className={estiloDeBoton('primario', 'flex-1')}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Invoice Modal */}
      {invoiceModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Adjuntar factura
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Adjunta tus documentos de factura aquí
                  </p>
                </div>
                <button
                  onClick={() => {
                    cerrarModalDeFactura();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-3 mb-4">
                {/* XML Upload - OBLIGATORIO */}
                <div className="border-2 border-gray-300 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-700">
                      Cargar archivo XML
                    </span>
                    <span className="text-red-500 font-bold">*</span>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".xml"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && !file.name.endsWith(".xml")) {
                          toast.error(
                            "Por favor selecciona un archivo XML válido"
                          );
                          return;
                        }
                        setXmlFile(file || null);
                      }}
                      className="hidden"
                    />
                    <span
                      className="inline-flex items-center gap-1.5 text-sm font-medium"
                      style={{ color: "#315FB9" }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Cargar
                    </span>
                  </label>
                </div>
                {xmlFile && (
                  <div className="flex items-center gap-2 ml-4">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="text-xs text-gray-600">{xmlFile.name}</p>
                  </div>
                )}

                {/* PDF Upload - OPCIONAL */}
                <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-700">
                      Cargar archivo PDF
                    </span>
                    <span className="text-xs text-gray-500">(opcional)</span>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && !file.name.endsWith(".pdf")) {
                          toast.error("Por favor selecciona un archivo PDF válido");
                          return;
                        }
                        setPdfFile(file || null);
                      }}
                      className="hidden"
                    />
                    <span
                      className="inline-flex items-center gap-1.5 text-sm font-medium"
                      style={{ color: "#315FB9" }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Cargar
                    </span>
                  </label>
                </div>
                {pdfFile && (
                  <div className="flex items-center gap-2 ml-4">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="text-xs text-gray-600">{pdfFile.name}</p>
                  </div>
                )}
              </div>

              {/* Paso de documentos de flete (SP-221): solo para tipo Fletes. */}
              {esFletes && (
                <div className="border-t border-gray-200 pt-4 mb-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Documentos de flete
                  </h4>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      ¿Esta factura incluye Carta Porte?
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCartaPorteAplica((valor) => !valor)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          cartaPorteAplica ? "bg-[#315FB9]" : "bg-gray-300"
                        }`}
                        aria-label="¿Esta factura incluye Carta Porte?"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            cartaPorteAplica ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600 w-5">
                        {cartaPorteAplica ? "Sí" : "No"}
                      </span>
                    </div>
                  </div>

                  {cartaPorteAplica ? (
                    <>
                      {/* La Carta Porte es un CFDI: van los dos archivos. */}
                      <ArchivoDeFlete
                        etiqueta="Carta Porte (XML)"
                        requerido
                        acepta=".xml"
                        archivo={cartaPorteXml}
                        onElegir={setCartaPorteXml}
                      />
                      <ArchivoDeFlete
                        etiqueta="Carta Porte (PDF)"
                        requerido
                        acepta=".pdf"
                        archivo={cartaPortePdf}
                        onElegir={setCartaPortePdf}
                      />
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Queda registrado que esta factura no incluye Carta Porte.
                    </p>
                  )}

                  <p className="text-xs text-gray-500 pt-1">
                    Soporte firmado: se requiere al menos uno de los dos.
                  </p>
                  <ArchivoDeFlete
                    etiqueta="Remisión firmada"
                    requerido={false}
                    acepta=".pdf"
                    archivo={remisionFirmada}
                    onElegir={setRemisionFirmada}
                  />
                  <ArchivoDeFlete
                    etiqueta="Hoja de entregas con acuse SLYRSA"
                    requerido={false}
                    acepta=".pdf"
                    archivo={hojaEntregas}
                    onElegir={setHojaEntregas}
                  />
                </div>
              )}

              <p className="text-xs text-gray-500 mb-6 ml-1">
                <span className="text-red-500">*</span> Campo obligatorio
              </p>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    cerrarModalDeFactura();
                  }}
                  className={estiloDeBoton('secundario', 'flex-1')}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleInvoiceSubmit}
                  disabled={!xmlFile || !fleteResuelto}
                  className={estiloDeBoton('primario', 'flex-1')}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* View Invoices Modal */}
      {viewInvoicesModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Facturas
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Orden {viewInvoicesOrderNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setViewInvoicesModalOpen(false);
                    setViewInvoicesOrderId(null);
                    setViewInvoicesOrderNumber("");
                    setInvoicesList([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingInvoices ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600">Cargando facturas...</p>
                  </div>
                </div>
              ) : invoicesList.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No hay facturas aún</p>
                  <button
                    onClick={async () => {
                      const order = orders.find(
                        (o) => o.id === viewInvoicesOrderId
                      );
                      if (!order) return;
                      if (!(await puedeFacturar())) return;
                      setViewInvoicesModalOpen(false);
                      setInvoiceOrderId(viewInvoicesOrderId);
                      setInvoiceModalOpen(true);
                    }}
                    className={estiloDeBoton('primario')}
                  >
                    <FileText className="w-4 h-4" />
                    Subir primera factura
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {invoicesList.map((invoice, index) => (
                    <TarjetaDeFactura
                      key={invoice.id}
                      invoice={invoice}
                      numero={index + 1}
                      onSubirComplemento={
                        viewInvoicesOrderId
                          ? () => {
                              setViewInvoicesModalOpen(false);
                              navigate(
                                `/proveedores/ordenes/${viewInvoicesOrderId}/pagos`
                              );
                            }
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer con botón subir nueva factura */}
            {invoicesList.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={async () => {
                    if (!(await puedeFacturar())) return;
                    setViewInvoicesModalOpen(false);
                    setInvoiceOrderId(viewInvoicesOrderId);
                    setInvoiceModalOpen(true);
                  }}
                  className={estiloDeBoton('primario', 'w-full')}
                >
                  <FileText className="w-4 h-4" />
                  Subir nueva factura
                </button>
              </div>
            )}
          </div>
        </CapaDeModal>
      )}

      {/* View Comments Modal */}
      {viewCommentsModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Comentarios
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Orden {viewCommentsOrderNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setViewCommentsModalOpen(false);
                    setViewCommentsOrderId(null);
                    setViewCommentsOrderNumber("");
                    setCommentsList([]);
                    setShowAddCommentInView(false);
                    setCommentText("");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingComments ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600">Cargando comentarios...</p>
                  </div>
                </div>
              ) : commentsList.length === 0 && !showAddCommentInView ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No hay comentarios aún</p>
                  <button
                    onClick={() => setShowAddCommentInView(true)}
                    className={estiloDeBoton('primario')}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Agregar primer comentario
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {commentsList.map((comment) => (
                    <div
                      key={comment.id}
                      className={`rounded-lg p-4 ${comment.author_type === "supplier"
                        ? "bg-blue-50 border border-blue-200 ml-8"
                        : "bg-gray-50 border border-gray-200 mr-8"
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${comment.author_type === "supplier"
                            ? "bg-blue-100"
                            : "bg-gray-200"
                            }`}
                        >
                          {comment.author_type === "supplier" ? (
                            <User className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Building2 className="w-4 h-4 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">
                              {comment.author_type === "supplier"
                                ? "Proveedor"
                                : "Cliente"}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDateTimeMX(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {comment.comment}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Formulario para agregar comentario */}
                  {showAddCommentInView && (
                    <div className="bg-gray-50 rounded-lg p-4 border-2 border-blue-300">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nuevo comentario
                      </label>
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Escribe tu comentario aquí..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none mb-3"
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setShowAddCommentInView(false);
                            setCommentText("");
                          }}
                          className={estiloDeBoton('secundario')}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleCommentSubmitInView}
                          disabled={!commentText.trim()}
                          className={estiloDeBoton('primario')}
                        >
                          Enviar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer con botón agregar comentario */}
            {!showAddCommentInView && commentsList.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowAddCommentInView(true)}
                  className={estiloDeBoton('primario', 'w-full')}
                >
                  <MessageSquare className="w-4 h-4" />
                  Agregar comentario
                </button>
              </div>
            )}
          </div>
        </CapaDeModal>
      )}

      {/* View Attachments Modal */}
      {viewAttachmentsModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Adjuntos
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Orden {viewAttachmentsOrderNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setViewAttachmentsModalOpen(false);
                    setViewAttachmentsOrderId(null);
                    setViewAttachmentsOrderNumber("");
                    setAttachmentsList([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingAttachments ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600">Cargando adjuntos...</p>
                  </div>
                </div>
              ) : attachmentsList.length === 0 ? (
                <div className="text-center py-12">
                  <File className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No hay archivos adjuntos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {attachmentsList.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="text-3xl">
                            {getFileIcon(attachment.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                              {attachment.description || "Sin descripción"}
                            </h4>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge color="morado">{getTypeLabel(attachment.type)}</Badge>
                              <span className="text-xs text-gray-500">
                                {formatDateTimeMX(attachment.created_at)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={attachment.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={estiloDeBoton('secundario')}
                              >
                                <Eye className="w-4 h-4" />
                                Ver
                              </a>
                              <a
                                href={attachment.file_url}
                                download
                                className={estiloDeBoton('secundario')}
                              >
                                <Download className="w-4 h-4" />
                                Descargar
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Attachment Modal */}
      {attachmentModalOpen && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Adjuntar archivo
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Adjunta un archivo para la orden {attachmentOrderNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setAttachmentModalOpen(false);
                    setAttachmentFile(null);
                    setAttachmentType("");
                    setAttachmentDescription("");
                    setAttachmentOrderId(null);
                    setAttachmentOrderNumber("");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isUploading}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Tipo de archivo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de archivo
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    value={attachmentType}
                    onChange={setAttachmentType}
                    disabled={isUploading}
                    opciones={[
                      { value: "cotizacion", label: "Cotización" },
                      { value: "especificaciones", label: "Especificaciones" },
                      { value: "contrato", label: "Contrato" },
                      { value: "evidencia", label: "Evidencia" },
                      { value: "otro", label: "Otro" },
                    ]}
                    placeholder="Seleccionar tipo (opcional)"
                    placeholderDeBusqueda="Buscar tipo de archivo..."
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    value={attachmentDescription}
                    onChange={(e) => setAttachmentDescription(e.target.value)}
                    placeholder="Descripción del archivo (opcional)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none"
                    style={{ backgroundColor: "#F9FAFB" }}
                    disabled={isUploading}
                  />
                </div>

                {/* Cargar archivo */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <div className="flex flex-col items-center justify-center">
                    {!attachmentFile ? (
                      <>
                        <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            onChange={(e) =>
                              setAttachmentFile(e.target.files?.[0] || null)
                            }
                            className="hidden"
                            disabled={isUploading}
                          />
                          <span
                            className={estiloDeBoton('primario')}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                              />
                            </svg>
                            Seleccionar archivo
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2">
                          Cualquier tipo de archivo
                        </p>
                      </>
                    ) : (
                      <>
                        <FileText className="w-8 h-8 text-blue-600 mb-2" />
                        <p className="text-sm font-medium text-gray-900 text-center">
                          {attachmentFile.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(attachmentFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          onClick={() => setAttachmentFile(null)}
                          className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                          disabled={isUploading}
                        >
                          Eliminar archivo
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setAttachmentModalOpen(false);
                    setAttachmentFile(null);
                    setAttachmentType("");
                    setAttachmentDescription("");
                    setAttachmentOrderId(null);
                    setAttachmentOrderNumber("");
                  }}
                  className={estiloDeBoton('secundario', 'flex-1')}
                  disabled={isUploading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAttachmentSubmit}
                  disabled={!attachmentFile || isUploading}
                  className={estiloDeBoton('primario', 'flex-1')}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                        />
                      </svg>
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-6 py-4">
          <div className="text-sm text-gray-600">
            Mostrando {(pagination.page - 1) * pagination.limit + 1} -{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} de{" "}
            {pagination.total} órdenes
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
              }
              disabled={pagination.page === 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from(
                { length: pagination.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => setPagination((prev) => ({ ...prev, page }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${pagination.page === page
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
              }
              disabled={pagination.page === pagination.totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* Bloqueo de facturacion: explica que lo detiene y como desbloquearse. */}
      <BloqueoFacturacion
        open={!!bloqueoError}
        error={bloqueoError}
        onClose={() => setBloqueoError(null)}
      />
    </div>
  );
};

export default PurchaseOrdersList;
